import { useMemo } from 'react';

import { usePetitionActionsPageData } from '#src/app/pages/actions/pageContext';
import type {
	PsfContactDecisionMaker,
	PsfContactDecisionMakerExperiment,
	PsfContactDecisionMakerExperimentVariation,
} from '#src/app/pages/actions/pageContext';

export function usePsfContactDecisionMakerExperiment(): UsePsfContactDecisionMakerExperimentResult {
	const {
		data: {
			petition: { decisionMakers },
			psfContactDecisionMakerExperiment,
		},
	} = usePetitionActionsPageData();

	return useMemo(() => {
		// GrowthBook targeting attributes can't express petition-specific eligibility, so we gate in
		// app code: only petitions with at least one decision maker are in the experiment population —
		// everyone else sees control. Matches the `psf_new_recs_screen_experiment` precedent.
		const [decisionMaker] = decisionMakers.nodes;
		const eligible = !!decisionMaker;
		// False positive: array-destructure typing gap — TS infers `decisionMaker` (and thus `eligible`) as
		// always-defined/always-true since array destructuring isn't typed as possibly-undefined here, but
		// `decisionMakers.nodes` can genuinely be empty at runtime (a petition with no decision makers),
		// which is exactly the case this eligibility check exists for.
		// oxlint-disable-next-line typescript/no-unnecessary-condition -- see comment above
		const variation = eligible ? psfContactDecisionMakerExperiment.variation : 'control';

		return {
			eligible,
			experiment: psfContactDecisionMakerExperiment,
			variation,
			decisionMaker,
			isTreatment: variation === 'treatment',
		};
	}, [decisionMakers, psfContactDecisionMakerExperiment]);
}

type UsePsfContactDecisionMakerExperimentResult = Readonly<{
	/** Whether the viewer is in the experiment population (petition has a decision maker). */
	eligible: boolean;
	/**
	 * The raw resolved GrowthBook experiment, for exposure tracking. It carries the real (ungated)
	 * GrowthBook assignment, so fire exposure off it ONLY when `eligible` is true — otherwise
	 * ineligible viewers (whom GrowthBook still buckets) would pollute the experiment. See
	 * `useTrackPsfContactDecisionMakerExposure`.
	 */
	experiment: PsfContactDecisionMakerExperiment;
	variation: PsfContactDecisionMakerExperimentVariation;
	decisionMaker: PsfContactDecisionMaker | undefined;
	isTreatment: boolean;
}>;
